// Switch — the on/off setting primitive, pure.
//
// A thin wrapper over @radix-ui/react-switch: Radix owns the hard a11y
// (role="switch", aria-checked, Space/Enter toggling, the labelled-control
// contract), we own only the look against foundations v1 (.claude/research/design/06).
//
// Two states, not a mode flag: `checked` is the setting's VALUE, and Radix's
// contract is a single controlled boolean + one change callback — this is a
// genuine two-position control, not a boolean that swaps the component's
// meaning, so it stays one component (the distinct-intent rule is about
// meaning-swapping flags, not a toggle's own value).
//
// Pure/props-driven: no internal state and no I/O — the on/off value arrives
// as `checked` and every change is reported UP via `onCheckedChange`; the
// consumer owns the state. Deps arrive via props.
//
// The ON state spends the accent (var(--color-accent)) — legitimate under
// the accent discipline because a switched-on toggle is a committed setting,
// the user's own decision, not chrome. State is NEVER colour-alone (SC
// 1.4.1): on/off is carried first by the thumb POSITION (right vs left) and
// only secondarily by the track colour.
//
// No intrinsic visible text, so no Lingui here — `label` is the accessible
// name the consumer supplies (already localised on their side) and is wired
// as aria-label, since this primitive renders only the control. A visible
// caption belongs to the consumer's row/Field, not to the switch.

import * as SwitchPrimitive from "@radix-ui/react-switch";
import "./Switch.css";

export type SwitchProps = {
    readonly checked: boolean;
    readonly onCheckedChange: (checked: boolean) => void;
    // The accessible name. Rendered as aria-label because the switch has no
    // visible text of its own; a real form control must never be nameless.
    readonly label: string;
    readonly disabled?: boolean;
};

export const Switch = ({
    checked,
    onCheckedChange,
    label,
    disabled,
}: SwitchProps) => (
    <SwitchPrimitive.Root
        className="bilda-switch"
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-label={label}
    >
        <SwitchPrimitive.Thumb className="bilda-switch__thumb" />
    </SwitchPrimitive.Root>
);
